import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Box, Button, Skeleton, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { EpisodeWord } from "../../../../Episode/services/episode.service";
import {
  TimeRange,
  findWordIndexAt,
  formatTimecode,
  speakerColor,
} from "../utils";

interface TranscriptSelectorProps {
  words: EpisodeWord[];
  isLoading: boolean;
  trim: TimeRange;
  activeIndex: number;
  followPlayback: boolean;
  onSelectRange: (start: number, end: number) => void;
  onSeek: (time: number) => void;
}

interface WordGroup {
  speaker?: number;
  start: number;
  indices: number[];
}

const CONTEXT_SECONDS = 45;
const EXPAND_SECONDS = 60;
const GROUP_GAP_SECONDS = 2;

function TranscriptSelector({
  words,
  isLoading,
  trim,
  activeIndex,
  followPlayback,
  onSelectRange,
  onSeek,
}: TranscriptSelectorProps) {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const preserveScrollFromBottomRef = useRef<number | null>(null);
  const hasScrolledToClipRef = useRef(false);
  const [windowRange, setWindowRange] = useState<TimeRange>([
    trim[0] - CONTEXT_SECONDS,
    trim[1] + CONTEXT_SECONDS,
  ]);

  useEffect(() => {
    setWindowRange(([from, to]) => [
      Math.min(from, trim[0] - CONTEXT_SECONDS / 2),
      Math.max(to, trim[1] + CONTEXT_SECONDS / 2),
    ]);
  }, [trim]);

  useEffect(() => {
    const word = words[activeIndex];
    if (!word) return;
    setWindowRange(([from, to]) => [
      Math.min(from, word.start - 5),
      Math.max(to, word.start + 30),
    ]);
  }, [activeIndex, words]);

  const firstIndex = Math.max(0, findWordIndexAt(words, windowRange[0]) + 1);
  const lastIndex = findWordIndexAt(words, windowRange[1]);
  const hasEarlier = firstIndex > 0;
  const hasLater = lastIndex < words.length - 1;

  const groups = useMemo(() => {
    const result: WordGroup[] = [];
    for (let i = firstIndex; i <= lastIndex; i += 1) {
      const word = words[i];
      const previous = words[i - 1];
      const current = result[result.length - 1];
      const startsNewGroup =
        !current ||
        word.speaker !== current.speaker ||
        (previous && word.start - previous.end > GROUP_GAP_SECONDS);
      if (startsNewGroup) {
        result.push({ speaker: word.speaker, start: word.start, indices: [i] });
      } else {
        current.indices.push(i);
      }
    }
    return result;
  }, [words, firstIndex, lastIndex]);

  const scrollWordIntoView = (index: number, position: number) => {
    const container = containerRef.current;
    const el = container?.querySelector<HTMLElement>(`[data-wi="${index}"]`);
    if (!container || !el) return;
    container.scrollTop = el.offsetTop - container.clientHeight * position;
  };

  useEffect(() => {
    if (hasScrolledToClipRef.current || !words.length) return;
    hasScrolledToClipRef.current = true;
    scrollWordIntoView(
      Math.max(0, findWordIndexAt(words, trim[0] + 0.01)),
      0.25,
    );
    // Only on first render with words; later trims must not yank the scroll.
  }, [words]);

  useEffect(() => {
    if (!followPlayback || activeIndex < 0) return;
    const container = containerRef.current;
    const el = container?.querySelector<HTMLElement>(
      `[data-wi="${activeIndex}"]`,
    );
    if (!container || !el) return;
    const top = el.offsetTop - container.scrollTop;
    if (top < 0 || top > container.clientHeight - 24) {
      scrollWordIntoView(activeIndex, 0.3);
    }
  }, [activeIndex, followPlayback]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const fromBottom = preserveScrollFromBottomRef.current;
    if (!container || fromBottom === null) return;
    container.scrollTop = container.scrollHeight - fromBottom;
    preserveScrollFromBottomRef.current = null;
  }, [firstIndex]);

  const loadEarlier = () => {
    const container = containerRef.current;
    if (container) {
      preserveScrollFromBottomRef.current =
        container.scrollHeight - container.scrollTop;
    }
    setWindowRange(([from, to]) => [from - EXPAND_SECONDS, to]);
  };

  const loadLater = () =>
    setWindowRange(([from, to]) => [from, to + EXPAND_SECONDS]);

  const handleMouseUp = () => {
    const container = containerRef.current;
    const selection = window.getSelection();
    if (!container || !selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) return;

    if (selection.isCollapsed) {
      const node = selection.anchorNode;
      const el = node instanceof Element ? node : node?.parentElement;
      const wordEl = el?.closest("[data-wi]");
      if (wordEl) onSeek(words[Number(wordEl.getAttribute("data-wi"))].start);
      return;
    }

    const selected = Array.from(
      container.querySelectorAll<HTMLElement>("[data-wi]"),
    ).filter((el) => {
      if (!range.intersectsNode(el)) return false;
      // A selection that merely touches a word's edge shouldn't include it.
      const text = el.firstChild;
      if (range.endContainer === text && range.endOffset === 0) return false;
      if (
        range.startContainer === text &&
        range.startOffset === (text?.textContent?.length ?? 0)
      ) {
        return false;
      }
      return true;
    });
    if (!selected.length) return;

    const first = Number(selected[0].getAttribute("data-wi"));
    const last = Number(selected[selected.length - 1].getAttribute("data-wi"));
    onSelectRange(words[first].start, words[last].end);
    selection.removeAllRanges();
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 1 }}>
        {Array.from({ length: 8 }, (_, i) => (
          <Skeleton key={i} height={22} width={`${70 + ((i * 13) % 30)}%`} />
        ))}
      </Box>
    );
  }

  if (!words.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          The transcript isn't available for this episode. Use the timeline
          below to set the clip.
        </Typography>
      </Box>
    );
  }

  const clipBg = alpha(theme.palette.primary.main, 0.16);

  return (
    <Box
      ref={containerRef}
      role="presentation"
      onMouseUp={handleMouseUp}
      sx={{
        position: "relative",
        height: "100%",
        overflowY: "auto",
        px: 1.5,
        py: 1,
        fontSize: 14,
        lineHeight: 1.9,
        cursor: "text",
        "& [data-wi]": {
          borderRadius: "3px",
          py: "1px",
          transition: "background-color 80ms",
        },
        "& [data-wi]:hover": { bgcolor: "grey.200" },
        "& .in-clip": { bgcolor: clipBg },
        "& .in-clip:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.26),
        },
        "& .clip-first": {
          boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
          pl: "4px",
        },
        "& .clip-last": {
          boxShadow: `inset -3px 0 0 ${theme.palette.primary.main}`,
          pr: "4px",
        },
        "& .active": {
          color: "primary.dark",
          fontWeight: 700,
          textDecoration: "underline",
          textDecorationThickness: "2px",
        },
        "& ::selection": { bgcolor: alpha(theme.palette.warning.main, 0.35) },
      }}
    >
      {hasEarlier && (
        <Box sx={{ textAlign: "center", mb: 1 }}>
          <Button size="small" onClick={loadEarlier}>
            Show earlier
          </Button>
        </Box>
      )}
      {groups.map((group) => (
        <Box
          key={group.indices[0]}
          sx={{ display: "flex", gap: 1.5, mb: 1.25, alignItems: "baseline" }}
        >
          <Box
            component="button"
            type="button"
            onMouseUp={(e: React.MouseEvent) => e.stopPropagation()}
            onClick={() => onSeek(group.start)}
            sx={{
              flexShrink: 0,
              width: 54,
              border: 0,
              p: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              textAlign: "left",
              fontSize: 11,
              fontVariantNumeric: "tabular-nums",
              color: "text.secondary",
              userSelect: "none",
              borderLeft: `3px solid ${speakerColor(group.speaker)}`,
              pl: 0.75,
              "&:hover": { color: "primary.main" },
            }}
          >
            {formatTimecode(group.start, false)}
          </Box>
          <Box sx={{ flex: 1 }}>
            {group.indices.map((i) => {
              const word = words[i];
              const inClip =
                word.start >= trim[0] - 0.01 && word.end <= trim[1] + 0.01;
              const prevInClip =
                inClip && i > 0 && words[i - 1].start >= trim[0] - 0.01;
              const nextInClip =
                inClip &&
                i < words.length - 1 &&
                words[i + 1].end <= trim[1] + 0.01;
              const classes = [
                inClip && "in-clip",
                inClip && !prevInClip && "clip-first",
                inClip && !nextInClip && "clip-last",
                i === activeIndex && "active",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <React.Fragment key={i}>
                  <span data-wi={i} className={classes || undefined}>
                    {word.text}
                  </span>{" "}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      ))}
      {hasLater && (
        <Box sx={{ textAlign: "center" }}>
          <Button size="small" onClick={loadLater}>
            Show later
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default memo(TranscriptSelector);
